<script lang="ts">
  import { invalidateAll } from '$app/navigation';
  import { page } from '$app/stores';
  import Button from '$components/Button.svelte';
  import DefaultStakingOptionsSelector from '$components/preferences/DefaultStakingOptionsSelector.svelte';
  import { updateSiteConfig } from '$utils/ui';
  import { onMount } from 'svelte';

  $: ({ user, hasCompanion } = $page.data);

  onMount(() => {
    if (
      user?.data &&
      user.data.user.siteConfig.prefill &&
      !user.data.user.siteConfig.prefill.defaults.thoroughbred.value
    ) {
      user.data.user.siteConfig.prefill.defaults.thoroughbred.value = 0;
    }
    if (
      user?.data &&
      user.data.user.siteConfig.prefill &&
      !user.data.user.siteConfig.prefill.defaults.harness.value
    ) {
      user.data.user.siteConfig.prefill.defaults.harness.value = 0;
    }
    if (
      user?.data &&
      user.data.user.siteConfig.prefill &&
      !user.data.user.siteConfig.prefill.defaults.greyhound.value
    ) {
      user.data.user.siteConfig.prefill.defaults.greyhound.value = 0;
    }
  });

  async function handleConfigChange() {
    if (!user?.data) return;

    // console.log(prefillThoroughbredDefaults);

    // user.data.user.siteConfig.prefill.defaults = {
    //   thoroughbred: prefillThoroughbredDefaults,
    //   harness: user.data.user.siteConfig.prefill.defaults.harness,
    //   greyhound: user.data.user.siteConfig.prefill.defaults.greyhound
    // };
    await updateSiteConfig();
    invalidateAll();
  }

  async function togglePrefill() {
    if (!user?.data || !user.data.user.siteConfig.prefill) return;
    user.data.user.siteConfig.prefill.quickFill =
      !user.data.user.siteConfig.prefill.quickFill;
    await updateSiteConfig();
    invalidateAll();
  }

  function detectBrowser() {
    // detect if the browser is chrome or firefox
    const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
    const isFirefox = navigator.userAgent.indexOf('Firefox') > -1;
    if (isChrome) return 'chrome';
    if (isFirefox) return 'firefox';
    return 'unknown';
  }

  function getExtensionUrl() {
    const browser = detectBrowser();
    if (browser === 'chrome') {
      return 'https://chrome.google.com/webstore/detail/betwatch-companion/cadelhhmlgiapdnlfhkidelbpgakfdol?hl=en&authuser=0';
    }
    if (browser === 'firefox') {
      return '';
    }
    return '';
  }
</script>

<div class="flex flex-row items-center gap-x-2">
  <div class="flex-1">
    <h2 class="text-base font-semibold leading-7 text-gray-900">
      Betslip Prefill Configuration
    </h2>
    <p class="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
      Betslip prefill allows you to automatically fill your betslip on a
      supported bookmaker website with a predefined stake, or a to win stake
      calculated from the current price.
    </p>
  </div>
</div>
{#if !$hasCompanion}
  <!-- Centered button to install -->
  <div class="flex h-full w-full flex-col place-items-center justify-center">
    <div
      class="flex flex-col items-center rounded border border-gray-200 bg-gray-100 p-5 shadow-sm"
    >
      <span class="w-2/3 pb-2 text-center text-sm text-gray-700">
        Betwatch requires a browser extension to automatically fill your betslip
        directly on the bookmaker website.
      </span>
      <span
        class="w-2/3 pb-3 text-center text-sm font-semibold text-gray-700 underline"
        >This is currently only supported in Google Chrome</span
      >
      {#if detectBrowser() === 'chrome'}
        <Button
          on:click={() => {
            // launch in new tab
            const url = getExtensionUrl();
            if (url) window.open(url, '_blank');
          }}>Install Companion Extension</Button
        >
        <span class="w-2/3 py-2 text-center text-sm text-gray-700"
          >You will need to refresh the page after installing</span
        >
      {/if}
    </div>
  </div>
{:else if user && user.data?.user.siteConfig.prefill}
  <div
    class="flex w-full flex-col space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0"
  >
    <button
      on:click|preventDefault|capture|stopPropagation={() => {
        togglePrefill();
      }}
      class="flex w-full cursor-pointer px-5 text-left hover:bg-gray-50 sm:gap-4 sm:py-6"
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="quickPrefill"
          class="cursor-pointer font-medium text-gray-700"
          ><span class="font-semibold">Quick Betslip Prefill</span>
          <p id="quickPrefill-description" class="cursor-pointer text-gray-500">
            Trigger the betslip prefill functionality by clicking on any
            displayed price
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <input
          on:click|preventDefault|capture|stopPropagation
          bind:checked={user.data.user.siteConfig.prefill.quickFill}
          id="quickPrefillEnabled"
          aria-describedby="quickPrefillEnabled-description"
          name="quickPrefillEnabled"
          type="checkbox"
          class="h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>
    </button>
    <div
      class="flex w-full cursor-pointer px-5 hover:bg-gray-50 sm:gap-4 sm:py-6"
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="betfairReductionFactor"
          class="cursor-pointer font-medium text-gray-700"
          ><span class="font-semibold">Thoroughbred Default</span>
          <p
            id="betfairReductionFactor-description"
            class="cursor-pointer text-gray-500"
          >
            {#if user.data.user.siteConfig.prefill.defaults.thoroughbred.type === 'STAKE'}
              The default stake amount to use when prefilling the betslip
            {:else}
              The default stake will be calculated based on the current price
              when prefilling the betslip
            {/if}
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <DefaultStakingOptionsSelector
          options={[
            { label: 'Stake', value: 'STAKE' },
            { label: 'To Win', value: 'TO_WIN' }
          ]}
          bind:activeValue={user.data.user.siteConfig.prefill.defaults
            .thoroughbred.type}
          on:change={() => {
            handleConfigChange();
          }}
        />
        <div class="relative">
          <span
            class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500"
            >$</span
          >
          <input
            bind:value={user.data.user.siteConfig.prefill.defaults.thoroughbred
              .value}
            on:blur={(value) => {
              handleConfigChange();
            }}
            id="prefillThoroughbredDefault"
            aria-describedby="prefillThoroughbredDefault-description"
            name="prefillThoroughbredDefault"
            type="number"
            step="1"
            class="w-24 cursor-pointer rounded border-gray-300 pl-7 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>

    <div
      class="flex w-full cursor-pointer px-5 hover:bg-gray-50 sm:gap-4 sm:py-6"
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="betfairReductionFactor"
          class="cursor-pointer font-medium text-gray-700"
          ><span class="font-semibold">Harness Default</span>
          <p
            id="betfairReductionFactor-description"
            class="cursor-pointer text-gray-500"
          >
            {#if user.data.user.siteConfig.prefill.defaults.harness.type === 'STAKE'}
              The default stake amount to use when prefilling the betslip
            {:else}
              The default stake will be calculated based on the current price
              when prefilling the betslip
            {/if}
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <DefaultStakingOptionsSelector
          options={[
            { label: 'Stake', value: 'STAKE' },
            { label: 'To Win', value: 'TO_WIN' }
          ]}
          bind:activeValue={user.data.user.siteConfig.prefill.defaults.harness
            .type}
          on:change={() => {
            handleConfigChange();
          }}
        />
        <div class="relative">
          <span
            class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500"
            >$</span
          >
          <input
            bind:value={user.data.user.siteConfig.prefill.defaults.harness
              .value}
            on:blur={(value) => {
              handleConfigChange();
            }}
            id="prefillHarnessDefault"
            aria-describedby="prefillHarnessDefault-description"
            name="prefillHarnessDefault"
            type="number"
            step="1"
            class="w-24 cursor-pointer rounded border-gray-300 pl-7 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>

    <div
      class="flex w-full cursor-pointer px-5 hover:bg-gray-50 sm:gap-4 sm:py-6"
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="betfairReductionFactor"
          class="cursor-pointer font-medium text-gray-700"
          ><span class="font-semibold">Greyhound Default</span>
          <p
            id="betfairReductionFactor-description"
            class="cursor-pointer text-gray-500"
          >
            {#if user.data.user.siteConfig.prefill.defaults.greyhound.type === 'STAKE'}
              The default stake amount to use when prefilling the betslip
            {:else}
              The default stake will be calculated based on the current price
              when prefilling the betslip
            {/if}
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <DefaultStakingOptionsSelector
          options={[
            { label: 'Stake', value: 'STAKE' },
            { label: 'To Win', value: 'TO_WIN' }
          ]}
          bind:activeValue={user.data.user.siteConfig.prefill.defaults.greyhound
            .type}
          on:change={() => {
            handleConfigChange();
          }}
        />
        <div class="relative">
          <span
            class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500"
            >$</span
          >
          <input
            type="text"
            bind:value={user.data.user.siteConfig.prefill.defaults.greyhound
              .value}
            on:blur={(value) => {
              handleConfigChange();
            }}
            id="prefillGreyhoundDefault"
            aria-describedby="prefillGreyhoundDefault-description"
            name="prefillGreyhoundDefault"
            step="1"
            class="w-24 cursor-pointer rounded border-gray-300 pl-7 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  </div>
{/if}
